/**
 * Utilidades de validación para formularios
 */

export const ESTADOS_MEXICO = [
  'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche',
  'Chiapas', 'Chihuahua', 'Ciudad de México', 'Coahuila de Zaragoza', 'Colima',
  'Durango', 'Estado de México', 'Guanajuato', 'Guerrero', 'Hidalgo',
  'Jalisco', 'Michoacán de Ocampo', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca',
  'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí', 'Sinaloa',
  'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz de Ignacio de la Llave',
  'Yucatán', 'Zacatecas'
];

/**
 * Valida que un código postal mexicano tenga exactamente 5 dígitos numéricos
 * @param {string} postalCode - El código postal a validar
 * @returns {Object} Objeto con isValid (boolean) y message (string)
 */
export const validatePostalCode = (postalCode) => {
  // Expresión regular para exactamente 5 dígitos numéricos
  const postalCodeRegex = /^\d{5}$/;
  
  if (!postalCode || postalCode.trim() === '') {
    return {
      isValid: false,
      message: 'El Código Postal es requerido'
    };
  }
  
  if (!postalCodeRegex.test(postalCode.trim())) {
    return {
      isValid: false,
      message: 'El Código Postal debe contener exactamente 5 dígitos numéricos.'
    };
  }
  
  return {
    isValid: true,
    message: ''
  };
};

/**
 * Valida código postal opcional (permite vacío pero valida formato si tiene contenido)
 * @param {string} postalCode - El código postal a validar
 * @returns {Object} Objeto con isValid (boolean) y message (string)
 */
export const validateOptionalPostalCode = (postalCode) => {
  // Si está vacío, es válido (opcional)
  if (!postalCode || postalCode.trim() === '') {
    return {
      isValid: true,
      message: ''
    };
  }
  
  // Si tiene contenido, debe ser exactamente 5 dígitos
  const postalCodeRegex = /^\d{5}$/;
  
  if (!postalCodeRegex.test(postalCode.trim())) {
    return {
      isValid: false,
      message: 'El Código Postal debe contener exactamente 5 dígitos numéricos.'
    };
  }
  
  return {
    isValid: true,
    message: ''
  };
};

/**
 * Valida RFC mexicano (formato básico)
 * @param {string} rfc - El RFC a validar
 * @returns {Object} Objeto con isValid (boolean) y message (string)
 */
export const validateRFCFormat = (rfc) => {
  if (!rfc || rfc.trim() === '') {
    return {
      isValid: true, // RFC es opcional
      message: ''
    };
  }
  
  // Expresión regular para RFC mexicano (persona moral y física)
  const rfcRegex = /^[A-Z&Ñ]{3,4}\d{6}[A-Z0-9]{3}$/;
  
  if (!rfcRegex.test(rfc.trim().toUpperCase())) {
    return {
      isValid: false,
      message: 'El RFC no tiene un formato válido. Debe tener 12 o 13 caracteres alfanuméricos.'
    };
  }
  
  return {
    isValid: true,
    message: ''
  };
};

/**
 * Valida correo electrónico
 * @param {string} email - El email a validar
 * @returns {Object} Objeto con isValid (boolean) y message (string)
 */
export const validateEmail = (email) => {
  if (!email || email.trim() === '') {
    return {
      isValid: false,
      message: 'El correo electrónico es requerido'
    };
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!emailRegex.test(email.trim())) {
    return {
      isValid: false,
      message: 'El correo electrónico no tiene un formato válido'
    };
  }
  
  return {
    isValid: true,
    message: ''
  };
};

/**
 * Valida que un campo no esté vacío
 * @param {string} value - El valor a validar
 * @param {string} fieldName - El nombre del campo para el mensaje de error
 * @returns {Object} Objeto con isValid (boolean) y message (string)
 */
export const validateRequired = (value, fieldName) => {
  if (!value || value.trim() === '') {
    return {
      isValid: false,
      message: `El ${fieldName} es requerido`
    };
  }
  
  return {
    isValid: true,
    message: ''
  };
};

/**
 * Valida que una geolocalización tenga el formato "latitud,longitud" con rangos válidos
 * @param {string} geolocation - La geolocalización a validar
 * @returns {Object} Objeto con isValid (boolean) y message (string)
 */
export const validateGeolocation = (geolocation) => {
  // Campo opcional - permitir vacío
  if (!geolocation || geolocation.trim() === '') {
    return {
      isValid: true,
      message: ''
    };
  }
  
  const trimmed = geolocation.trim();
  const parts = trimmed.split(',');
  
  // Debe haber exactamente 2 valores separados por coma
  if (parts.length !== 2) {
    return {
      isValid: false,
      message: 'Coordenada inválida'
    };
  }
  
  try {
    const latitude = parseFloat(parts[0].trim());
    const longitude = parseFloat(parts[1].trim());
    
    // Validar rangos: latitud (-90 a 90), longitud (-180 a 180)
    if (latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180) {
      return {
        isValid: true,
        message: ''
      };
    } else {
      return {
        isValid: false,
        message: 'Coordenada inválida'
      };
    }
    
  } catch (error) {
    return {
      isValid: false,
      message: 'Coordenada inválida'
    };
  }
};
